<template>
  <v-container fluid>
    <alert-unsaved-changes v-if="unsavedChanges && edit"></alert-unsaved-changes>
    <v-form ref="basicInfoForm">
      <!-- Title and Number -->
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('patent-title') }}</label>
          <p class="label-info fixed-label-height">
            The formal title given to the patent, which describes the invention.
          </p>
          <v-text-field
            v-model.trim="patent.title"
            :rules="minMaxLengthRule(4, 200)"
            class="required"
            outlined
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('patent-number') }}</label>
          <p class="label-info fixed-label-height">A unique identifier assigned to the patent</p>
          <v-text-field
            v-model.trim="patent.patentNumber"
            :rules="minMaxLengthRule(4, 100)"
            class="required"
            outlined
            clearable
          ></v-text-field>
        </v-col>
      </v-row>
      <!-- Organisation and Status -->
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('patent-organisation') }}</label>
          <p class="label-info fixed-label-height">
            The name of the organisation that manages or delivers the Patent
          </p>
          <v-autocomplete
            v-if="role === 'super-admin'"
            v-model="autocomplete.organisation"
            :items="organisations"
            :item-value="'id'"
            :item-text="'name'"
            return-object
            outlined
            clearable
          ></v-autocomplete>
          <v-text-field
            v-else
            v-model="patent.organisation.name"
            outlined
            :disabled="true"
            :rules="requiredRule()"
            class="required"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('patent-status') }}</label>
          <p class="label-info fixed-label-height">
            Current legal status of the patent (e.g., pending, granted, expired, or lapsed).
          </p>
          <v-autocomplete
            v-model="patent.patentStatus"
            :items="patentStatusItems"
            :placeholder="'Patent Status'"
            :item-value="'id'"
            :item-text="'name'"
            class="required"
            :rules="requiredRule()"
            return-object
            outlined
            clearable
          >
          </v-autocomplete>
        </v-col>
      </v-row>
      <!-- Application Number and Owner -->
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('application-number') }}</label>
          <p class="label-info fixed-label-height">
            The identifier given to the patent application before the patent is granted.
          </p>
          <v-text-field
            v-model.trim="patent.applicationNumber"
            :rules="minMaxLengthRule(4, 100)"
            class="required"
            outlined
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <div v-for="(owner, index) in patent.owners" :key="index" class="mb-4">
            <v-row align="center">
              <v-col :cols="patent.owners.length > 1 ? 11 : 12">
                <label>{{ $t('assignee-or-owner') }}</label>
                <p class="label-info fixed-label-height">
                  The individual or entity that holds the rights to the patent.
                </p>
                <v-text-field
                  v-model.trim="patent.owners[index]"
                  :rules="minMaxLengthRule(4, 100)"
                  class="required"
                  outlined
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="1" class="d-flex justify-end mt-5" v-if="patent.owners.length > 1">
                <v-btn fab small text @click="removeOwner(index)">
                  <v-icon>
                    $vuetify.icons.trash
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <!--Add Owner -->
            <v-row v-if="hasNonEmptyOwners" class="mt-0 pt-0">
              <v-col cols="4">
                <v-btn
                  @click="addOwner"
                  depressed
                  rounded
                  medium
                  color="primary200"
                  class="white--text font-weight-bold"
                >
                  <v-icon left>
                    $vuetify.icons.plus
                  </v-icon>
                  <span class="text-decoration-underline text-transform-none font-weight-bold">
                    {{ $t('Add Owner') }}
                  </span>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <!-- Inventor and Office -->
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <div v-for="(inventor, index) in patent.inventors" :key="index" class="mb-4">
            <v-row align="center">
              <v-col :cols="patent.inventors.length > 1 ? 11 : 12">
                <label>{{ $t('inventor(s)') }}</label>
                <p class="label-info fixed-label-height">
                  The name(s) of the person(s) who created the invention.
                </p>
                <v-text-field
                  v-model.trim="patent.inventors[index]"
                  :rules="minMaxLengthRule(4, 100)"
                  class="required"
                  outlined
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="1" class="d-flex justify-end mt-5" v-if="patent.inventors.length > 1">
                <v-btn fab small text @click="removeInventor(index)">
                  <v-icon>
                    $vuetify.icons.trash
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <!--Add Inventor -->
            <v-row v-if="hasNonEmptyInventors" class="mt-0 pt-0">
              <v-col cols="4">
                <v-btn
                  @click="addInventor"
                  depressed
                  rounded
                  medium
                  color="primary200"
                  class="white--text font-weight-bold"
                >
                  <v-icon left>
                    $vuetify.icons.plus
                  </v-icon>
                  <span class="text-decoration-underline text-transform-none font-weight-bold">
                    {{ $t('Add Inventor') }}
                  </span>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <div v-for="(office, index) in patent.patentOffices" :key="index" class="mb-4">
            <v-row align="center">
              <v-col :cols="patent.patentOffices.length > 1 ? 11 : 12">
                <label>{{ $t('patent-office') }}</label>
                <p class="label-info fixed-label-height">
                  The governmental body or office where the patent was filed and registered.
                </p>
                <v-text-field
                  v-model.trim="patent.patentOffices[index]"
                  :rules="minMaxLengthRule(4, 100)"
                  class="required"
                  outlined
                  clearable
                ></v-text-field>
              </v-col>
              <v-col
                cols="1"
                class="d-flex justify-end mt-5"
                v-if="patent.patentOffices.length > 1"
              >
                <v-btn fab small text @click="removeOffice(index)">
                  <v-icon>
                    $vuetify.icons.trash
                  </v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <!--Add Office -->
            <v-row v-if="hasNonEmptyOffices" class="mt-0 pt-0">
              <v-col cols="4">
                <v-btn
                  @click="addOffice"
                  depressed
                  rounded
                  medium
                  color="primary200"
                  class="white--text font-weight-bold"
                >
                  <v-icon left>
                    $vuetify.icons.plus
                  </v-icon>
                  <span class="text-decoration-underline text-transform-none font-weight-bold">
                    {{ $t('Add Office') }}
                  </span>
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>
      <!-- Filling Date and Issue Date -->
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('filling-date') }}</label>
          <p class="label-info fixed-label-height">
            The date on which the patent application was officially filed with the patent office.
          </p>
          <v-menu
            v-model="dateFromMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                outlined
                v-model="patent.fillingDate"
                label="Date From"
                append-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                class="required"
                :rules="requiredRule()"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="patent.fillingDate"
              @input="dateFromMenu = false"
              color="rgba(157, 87, 244, 1)"
              :first-day-of-week="1"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('issue-date') }}</label>
          <p class="label-info fixed-label-height">
            The date on which the patent was granted by the patent office.
          </p>
          <v-menu
            v-model="dateIssueMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                outlined
                v-model="patent.issueDate"
                label="Issue Date"
                append-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                class="required"
                :rules="requiredRule()"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="patent.issueDate"
              @input="dateIssueMenu = false"
              color="rgba(157, 87, 244, 1)"
              :first-day-of-week="1"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
      <!-- Expiration Date -->
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>{{ $t('expiration-date') }}</label>
          <p class="label-info">
            The date on which the patent will expire.
          </p>
          <v-menu
            v-model="dateExpirationMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                outlined
                v-model="patent.expirationDate"
                label="Expiration Date"
                append-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                :class="requiredClass"
                :rules="requiredExpirationRule()"
                clearable
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="patent.expirationDate"
              @input="dateExpirationMenu = false"
              color="rgba(157, 87, 244, 1)"
              :first-day-of-week="1"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>
      <!-- License Row -->
      <v-row>
        <v-col cols="12" sm="12" md="6">
          <label>Can be licensed</label>
          <p class="label-info">
            The license button is available only in patents that have this enabled.
          </p>
          <v-switch v-model="patent.applyForLicense" color="success" class="required"></v-switch>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="my-5" justify="end">
        <v-btn
          v-if="unsavedChanges && edit"
          @click="cancel"
          outlined
          class="black--text font-weight-bold mx-2"
          rounded
          x-large
        >
          {{ $t('cancel') }}
        </v-btn>
        <v-btn
          v-if="unsavedChanges && edit"
          @click="updatePatentBasicInfo"
          depressed
          rounded
          x-large
          color="primary200"
          class="white--text font-weight-bold"
        >
          {{ $t('update') }}
        </v-btn>
        <v-btn
          v-else
          depressed
          rounded
          x-large
          color="primary200"
          @click="next"
          class="white--text font-weight-bold"
        >
          {{ $t('next') }}
          <v-icon color="white">$vuetify.icons.next</v-icon>
        </v-btn>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import util from '@/mixins/util.js';
import AlertUnsavedChanges from '@/components/shared/helpers/AlertUnsavedChanges';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'PatentBasicInfo',
  components: { AlertUnsavedChanges },
  mixins: [util],
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    published: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      patent: {
        title: '',
        patentNumber: '',
        organisation: {
          id: null,
          name: '',
        },
        patentStatus: {
          id: null,
          name: '',
        },
        applicationNumber: '',
        owners: [''],
        inventors: [''],
        patentOffices: [''],
        fillingDate: null,
        issueDate: null,
        expirationDate: null,
        applyForLicense: true,
      },
      role: '',
      organisations: [],
      autocomplete: {
        organisation: {
          id: null,
          name: '',
        },
      },
      initialAutocompleteOrganisation: {},
      patentStatusItems: [],
      dateFromMenu: false,
      dateIssueMenu: false,
      dateExpirationMenu: false,
      unsavedChanges: false,
      dataLoaded: false,
      status: false,
      initialStatus: {},
      initialPatent: {},
    };
  },
  watch: {
    patent: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.patent.title !== this.initialPatent.title ||
          this.patent.patentNumber !== this.initialPatent.patentNumber ||
          this.patent.patentStatus.id !== this.initialPatent.patentStatus.id ||
          this.patent.applicationNumber !== this.initialPatent.applicationNumber ||
          this.patent.fillingDate !== this.initialPatent.fillingDate ||
          this.patent.issueDate !== this.initialPatent.issueDate ||
          this.patent.expirationDate !== this.initialPatent.expirationDate ||
          this.patent.applyForLicense !== this.initialPatent.applyForLicense ||
          JSON.stringify(this.patent.owners) !== JSON.stringify(this.initialPatent.owners) ||
          JSON.stringify(this.patent.inventors) !== JSON.stringify(this.initialPatent.inventors) ||
          JSON.stringify(this.patent.patentOffices) !==
            JSON.stringify(this.initialPatent.patentOffices);
      },
    },
    published: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges = this.status !== this.published;
      },
    },
    autocomplete: {
      deep: true,
      handler() {
        if (!this.dataLoaded) return;
        this.unsavedChanges =
          this.autocomplete.organisation.id !==
            this.initialAutocompleteOrganisation.organisation.id ||
          this.autocomplete.organisation.name !==
            this.initialAutocompleteOrganisation.organisation.name;
      },
    },
  },
  mounted() {
    this.role = localStorage.getItem('roles') || '';
    console.log('role:', this.role);

    if (this.role === 'super-admin') {
      this.axios
        .get('/api/v1/providers/all-light')
        .then(res => {
          this.organisations = res.data;
        })
        .finally(() => {
          this.loadingComponents++;
        });
    } else {
      this.axios
        .get('/api/v1/provider-settings/' + sessionStorage.getItem('providerId') + '/light')
        .then(res => {
          this.patent.organisation.name = res.data.name;
          this.patent.organisation.id = res.data.id;
        });
    }

    this.axios.get('/api/v1/patent-status').then(res => {
      this.patentStatusItems = res.data.content;
    });

    if (this.edit) {
      this.getPatentBasicInfo();
    }
  },
  computed: {
    hasNonEmptyOwners() {
      return this.patent.owners.some(owner => owner.trim() !== '');
    },
    hasNonEmptyInventors() {
      return this.patent.inventors.some(inventor => inventor.trim() !== '');
    },
    hasNonEmptyOffices() {
      return this.patent.patentOffices.some(office => office.trim() !== '');
    },
    requiredClass() {
      return this.patent.patentStatus.name === 'Pending' ? '' : 'required';
    },
  },
  methods: {
    requiredExpirationRule() {
      if (this.patent.patentStatus.name === 'Pending') {
        return;
      }
      return [v => !!v || this.$i18n.t('required-field')];
    },
    addOwner() {
      this.patent.owners.push('');
    },
    removeOwner(index) {
      this.patent.owners.splice(index, 1);
    },
    addInventor() {
      this.patent.inventors.push('');
    },
    removeInventor(index) {
      this.patent.inventors.splice(index, 1);
    },
    addOffice() {
      this.patent.patentOffices.push('');
    },
    removeOffice(index) {
      this.patent.patentOffices.splice(index, 1);
    },
    next() {
      const isFormValid = this.$refs.basicInfoForm.validate();
      if (isFormValid) {
        this.$emit('tab', 1);
        this.$emit('patentBasicInfo', { ...this.patent });
      }
    },
    getPatentBasicInfo() {
      EventBus.$emit('waiting', true);
      const id = this.$route.params.id;

      this.axios
        .get(`/api/v1/patent/${id}`)
        .then(response => {
          console.log('Get Patent Basic Info', response.data);

          this.status = response.data.published;
          this.patent.title = response.data.title;
          this.patent.patentNumber = response.data.patentNumber;
          this.patent.patentStatus = {
            id: response.data.patentStatus.id,
            name: response.data.patentStatus.name,
          };
          this.patent.applicationNumber = response.data.applicationNumber;
          this.patent.owners = response.data.owners;
          this.patent.inventors = response.data.inventors;
          this.patent.patentOffices = response.data.patentOffices;
          this.patent.fillingDate = response.data.fillingDate;
          this.patent.issueDate = response.data.issueDate;
          this.patent.expirationDate = response.data.expirationDate;
          this.patent.applyForLicense = response.data.applyForLicense;

          if (this.role === 'super-admin') {
            this.autocomplete.organisation = {
              id: response.data.organisation.id,
              name: response.data.organisation.name,
            };

            this.initialAutocompleteOrganisation = {
              organisation: {
                id: response.data.organisation.id,
                name: response.data.organisation.name,
              },
            };
          }

          this.initialPatent = {
            title: response.data.title,
            patentNumber: response.data.patentNumber,
            patentStatus: {
              id: response.data.patentStatus.id,
              name: response.data.patentStatus.name,
            },
            applicationNumber: response.data.applicationNumber,
            owners: [...response.data.owners],
            inventors: [...response.data.inventors],
            patentOffices: [...response.data.patentOffices],
            fillingDate: response.data.fillingDate,
            issueDate: response.data.issueDate,
            expirationDate: response.data.expirationDate,
            applyForLicense: response.data.applyForLicense,
          };

          this.initialStatus = {
            published: response.data.published,
          };

          this.$emit('status', this.initialStatus.published);
          this.dataLoaded = true;
        })
        .catch(error => {
          console.error('Error fetching patent data:', error);
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    updatePatentBasicInfo() {
      const id = this.$route.params.id;

      if (this.role === 'provider') {
        const patentUpdateData = {
          published: this.published,
          id: id,
          title: this.patent.title,
          applicationNumber: this.patent.applicationNumber,
          organisation: this.patent.organisation,
          patentNumber: this.patent.patentNumber,
          patentStatus: {
            id: this.patent.patentStatus.id,
            name: this.patent.patentStatus.name,
          },
          owners: [...this.patent.owners],
          inventors: [...this.patent.inventors],
          patentOffices: [...this.patent.patentOffices],
          fillingDate: this.patent.fillingDate,
          issueDate: this.patent.issueDate,
          expirationDate: this.patent.expirationDate,
          applyForLicense: this.patent.applyForLicense,
        };

        const isFormValid = this.$refs.basicInfoForm.validate();
        if (isFormValid) {
          this.axios
            .put('api/v1/patent/basic-info', patentUpdateData)
            .then(response => {
              console.log('Patent info updated successfully:', response.data);
              EventBus.$emit('snackbar', {
                text: 'Patent Updated Successfully',
              });
              this.initialPatent = { ...this.patent };
              this.initialStatus = this.status;
              this.unsavedChanges = false;
              this.$emit('updatedChanges', true);

              this.getPatentBasicInfo();
            })
            .catch(error => {
              console.error('Error updating patent info:', error);
            });
        }
      }

      if (this.role === 'super-admin') {
        const patentUpdateData = {
          published: this.published,
          id: id,
          title: this.patent.title,
          applicationNumber: this.patent.applicationNumber,
          organisation: {
            id: this.autocomplete.organisation.id,
            name: this.autocomplete.organisation.name,
          },
          patentNumber: this.patent.patentNumber,
          patentStatus: {
            id: this.patent.patentStatus.id,
            name: this.patent.patentStatus.name,
          },
          owners: [...this.patent.owners],
          inventors: [...this.patent.inventors],
          patentOffices: [...this.patent.patentOffices],
          fillingDate: this.patent.fillingDate,
          issueDate: this.patent.issueDate,
          expirationDate: this.patent.expirationDate,
          applyForLicense: this.patent.applyForLicense,
        };

        const isFormValid = this.$refs.basicInfoForm.validate();
        if (isFormValid) {
          this.axios
            .put('api/v1/patent/basic-info', patentUpdateData)
            .then(response => {
              console.log('Patent info updated successfully:', response.data);
              EventBus.$emit('snackbar', {
                text: 'Patent Updated Successfully',
              });
              this.initialPatent = { ...this.patent };
              this.initialStatus = this.status;
              this.initialAutocompleteOrganisation = { ...this.autocomplete };
              this.unsavedChanges = false;
              this.$emit('updatedChanges', true);

              this.getPatentBasicInfo();
            })
            .catch(error => {
              console.error('Error updating patent info:', error);
            });
        }
      }
    },
    cancel() {
      this.getPatentBasicInfo();
    },
  },
};
</script>

<style scoped>
.fixed-label-height {
  min-height: 3em;
}
</style>
